import { Routes, Router } from '@angular/router';
import { inject } from '@angular/core';
import { authGuard, guestGuard } from '@features/shared/auth/presentation/guards/auth.guard';
import {
  onboardingGuard,
  onboardingRequiredGuard,
} from '@features/web/onboarding/presentation/guards/onboarding.guard';
import {
  clinicSelectionRequiredGuard,
  clinicSelectorGuard,
} from '@features/web/clinic-selector/presentation/guards/clinic-selector.guard';
import { clinicWarmupGuard } from '@features/web/clinic-warmup/presentation/guards/clinic-warmup.guard';
import { roleGuard } from '@features/shared/auth/presentation/guards/role.guard';
import { hasModuleAccessGuard } from '@features/web/auth-web/presentation/guards/module-access.guard';
import { settingsUnsavedChangesGuard } from '@features/web/settings/presentation/guards/settings-unsaved-changes.guard';
import { UserRole } from '@features/shared/auth/domain/models/authenticated-user.model';
import { AppRoutes } from '@shared/constants/routes.enum';
import { WebRoutes } from '@shared/constants/web-routes.enum';
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from '@shared/constants/legal-links.const';
import { externalRedirectGuard } from '@shared/routing/external-redirect.guard';

export const webRoutes: Routes = [
  {
    path: WebRoutes.AccountDeletion.slice(1),
    data: { titleKey: 'accountDeletionTitle' },
    loadComponent: () =>
      import('@features/web/account-deletion/presentation/pages/account-deletion-request/account-deletion-request.page').then(
        (m) => m.AccountDeletionRequestPage,
      ),
  },
  {
    path: WebRoutes.AccountDeletionConfirm.slice(1),
    data: { titleKey: 'accountDeletionConfirm', deletionAction: 'confirm' },
    loadComponent: () =>
      import('@features/web/account-deletion/presentation/pages/account-deletion-link/account-deletion-link.page').then(
        (m) => m.AccountDeletionLinkPage,
      ),
  },
  {
    path: WebRoutes.AccountDeletionCancel.slice(1),
    data: { titleKey: 'accountDeletionCancel', deletionAction: 'cancel' },
    loadComponent: () =>
      import('@features/web/account-deletion/presentation/pages/account-deletion-link/account-deletion-link.page').then(
        (m) => m.AccountDeletionLinkPage,
      ),
  },
  {
    path: WebRoutes.Support.slice(1),
    data: { titleKey: 'accountDeletionSupportTitle' },
    loadComponent: () =>
      import('@features/web/account-deletion/presentation/pages/account-support/account-support.page').then(
        (m) => m.AccountSupportPage,
      ),
  },
  {
    path: 'email/unsubscribe',
    data: { titleKey: 'commercialEmailUnsubscribePageTitle' },
    loadComponent: () =>
      import('./pages/commercial-email-unsubscribe/commercial-email-unsubscribe.page').then(
        (m) => m.CommercialEmailUnsubscribePage,
      ),
  },
  {
    path: WebRoutes.CookiePolicy.slice(1),
    data: { titleKey: 'cookiePolicyTitle' },
    loadComponent: () =>
      import('./pages/cookie-policy/cookie-policy.page').then((m) => m.CookiePolicyPage),
  },
  // Previously shared panel URLs keep working; the documents themselves live on the landing.
  {
    path: WebRoutes.PrivacyPolicy.slice(1),
    canActivate: [externalRedirectGuard(PRIVACY_POLICY_URL)],
    children: [],
  },
  {
    path: WebRoutes.TermsOfUse.slice(1),
    canActivate: [externalRedirectGuard(TERMS_OF_USE_URL)],
    children: [],
  },
  {
    path: AppRoutes.Login.slice(1),
    canActivate: [guestGuard],
    data: { titleKey: 'loginPageTitle' },
    loadComponent: () =>
      import('@features/web/auth-web/presentation/pages/web-login/web-login.page').then(
        (m) => m.WebLoginPage,
      ),
  },
  {
    path: AppRoutes.RequestAccess.slice(1),
    canActivate: [guestGuard],
    data: { titleKey: 'requestAccessTitle' },
    loadComponent: () =>
      import('@features/shared/access-requests/presentation/pages/access-request/access-request.page').then(
        (m) => m.AccessRequestPage,
      ),
  },
  {
    path: AppRoutes.ForgotPassword.slice(1),
    canActivate: [guestGuard],
    data: { titleKey: 'forgotPasswordTitle' },
    loadComponent: () =>
      import('@features/shared/auth/presentation/pages/forgot-password/forgot-password.page').then(
        (m) => m.ForgotPasswordPage,
      ),
  },
  {
    path: WebRoutes.ResetPassword.slice(1),
    data: { titleKey: 'resetPasswordPageTitle' },
    loadComponent: () =>
      import('@features/web/auth-web/presentation/pages/reset-password/reset-password.page').then(
        (m) => m.ResetPasswordPage,
      ),
  },
  {
    path: WebRoutes.CompleteClinicRegistration.slice(1),
    data: { titleKey: 'completeClinicRegistrationPageTitle' },
    loadComponent: () =>
      import('@features/web/auth-web/presentation/pages/complete-clinic-registration/complete-clinic-registration.page').then(
        (m) => m.CompleteClinicRegistrationPage,
      ),
  },
  // Guardless on purpose, like its two neighbours above: the tutor arrives here straight from an
  // email, with no session. It reads nothing from the URL and redeems nothing -- see the page's own
  // remarks.
  {
    path: WebRoutes.PetOwnerAppInvitation.slice(1),
    data: { titleKey: 'petOwnerAppInvitationTitle' },
    loadComponent: () =>
      import('@features/web/auth-web/presentation/pages/pet-owner-app-invitation/pet-owner-app-invitation.page').then(
        (m) => m.PetOwnerAppInvitationPage,
      ),
  },
  {
    path: AppRoutes.AccessDenied.slice(1),
    canActivate: [authGuard],
    data: { titleKey: 'accessDeniedPageTitle' },
    loadComponent: () =>
      import('./pages/web-access-denied/web-access-denied.page').then((m) => m.WebAccessDeniedPage),
  },
  // Kept only so bookmarks of the retired standalone account page still land somewhere: passkey
  // management now lives in the settings page's "Cuenta" section.
  {
    path: WebRoutes.Account.slice(1),
    pathMatch: 'full',
    redirectTo: WebRoutes.Settings,
  },
  {
    path: WebRoutes.Onboarding.slice(1),
    canActivate: [authGuard, onboardingGuard],
    data: { titleKey: 'onboardingPageTitle' },
    loadComponent: () =>
      import('@features/web/onboarding/presentation/pages/onboarding/onboarding.page').then(
        (m) => m.OnboardingPage,
      ),
  },
  {
    path: WebRoutes.ClinicSelector.slice(1),
    canActivate: [authGuard, clinicSelectorGuard],
    data: { titleKey: 'clinicSelectorPageTitle' },
    loadComponent: () =>
      import('@features/web/clinic-selector/presentation/pages/clinic-selector/clinic-selector.page').then(
        (m) => m.ClinicSelectorPage,
      ),
  },
  // Outside the admin shell on purpose, like the clinic selector above: the whole point of this
  // screen is to fill the feature stores and the backend cache BEFORE `AdminShellPage` and its own
  // eager calls construct. Only the staff roles that actually have a panel to warm up.
  {
    path: WebRoutes.Warmup.slice(1),
    canActivate: [
      authGuard,
      roleGuard([UserRole.Admin, UserRole.ClinicOwner, UserRole.Worker]),
      // After `authGuard`, which is what leaves the user resolved for the freshness check.
      clinicWarmupGuard,
    ],
    data: { titleKey: 'clinicWarmupPageTitle' },
    loadComponent: () =>
      import('@features/web/clinic-warmup/presentation/pages/clinic-warmup/clinic-warmup.page').then(
        (m) => m.ClinicWarmupPage,
      ),
  },
  // Outside the admin shell on purpose: errorNotificationInterceptor lands every staff member of a
  // clinic without a usable subscription (backend 402 `subscription-required`) here, and the shell's
  // own eager API calls would immediately 402 again.
  {
    path: WebRoutes.SubscriptionRequired.slice(1),
    canActivate: [authGuard],
    data: { titleKey: 'subscriptionRequiredTitle' },
    loadComponent: () =>
      import('@features/web/platform-billing/presentation/pages/subscription-required/subscription-required.page').then(
        (m) => m.SubscriptionRequiredPage,
      ),
  },
  // A SIBLING of the screen above, not a child of the admin shell: this is the page that pays the
  // debt or buys a new subscription, so mounting it inside the shell would put it behind the very
  // eager calls the 402 gate rejects -- which is exactly what made "Revisar facturación" a dead end
  // when it pointed at Ajustes > Suscripción. Only the ClinicOwner: nobody else can pay.
  {
    path: WebRoutes.SubscriptionRecovery.slice(1),
    canActivate: [authGuard, roleGuard([UserRole.ClinicOwner])],
    data: { titleKey: 'subscriptionRecoveryTitle' },
    loadComponent: () =>
      import('@features/web/platform-billing/presentation/pages/subscription-recovery/subscription-recovery.page').then(
        (m) => m.SubscriptionRecoveryPage,
      ),
  },
  {
    path: WebRoutes.SuperAdmin.slice(1),
    canActivate: [authGuard, roleGuard([UserRole.SuperAdmin, UserRole.Developer])],
    loadComponent: () =>
      import('./shells/superadmin-shell/superadmin-shell.page').then((m) => m.SuperAdminShellPage),
    children: [
      {
        path: '',
        data: { titleKey: 'superAdminNavDashboard' },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/superadmin-dashboard/superadmin-dashboard.page').then(
            (m) => m.SuperAdminDashboardPage,
          ),
      },
      {
        path: 'clinicas',
        data: {
          titleKey: 'superAdminNavClinics',
          icon: 'building-2',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/clinics/clinics.page').then(
            (m) => m.ClinicsPage,
          ),
      },
      // A sibling of `clinicas`, not a child of it: nesting would leave `clinicas` a children-only
      // route with no `loadComponent` of its own. The shell reads its active nav key off the third
      // URL segment, so both paths keep Clínicas highlighted either way.
      {
        path: 'clinicas/:id',
        // The page opens on its own breadcrumb and clinic header, so the shell's title bar would
        // only stack a second, emptier header on top of it.
        data: {
          titleKey: 'superAdminNavClinics',
          icon: 'building-2',
          hidesShellHeader: true,
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/clinic-detail/clinic-detail.page').then(
            (m) => m.ClinicDetailPage,
          ),
      },
      {
        path: 'planes-comerciales',
        data: {
          titleKey: 'superAdminNavCommercialPlans',
          icon: 'package',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/commercial-plans/commercial-plans.page').then(
            (m) => m.CommercialPlansPage,
          ),
      },
      {
        path: 'facturacion-pagos',
        data: {
          titleKey: 'superAdminNavBillingPayments',
          icon: 'receipt',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/billing-payments/billing-payments.page').then(
            (m) => m.BillingPaymentsPage,
          ),
      },
      {
        path: 'onboarding',
        data: {
          titleKey: 'superAdminNavOnboarding',
          icon: 'rocket',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/onboarding-board/onboarding-board.page').then(
            (m) => m.OnboardingBoardPage,
          ),
      },
      {
        path: 'incidencias-soporte',
        data: {
          titleKey: 'superAdminNavSupport',
          icon: 'life-buoy',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/superadmin-support/superadmin-support.page').then(
            (m) => m.SuperAdminSupportPage,
          ),
      },
      {
        path: 'denuncias-chat',
        pathMatch: 'full',
        redirectTo: ({ queryParams }) =>
          inject(Router).createUrlTree(['/superadmin/incidencias-soporte'], {
            queryParams: { ...queryParams, tipo: 'chat_abuse' },
          }),
      },
      {
        path: 'analitica',
        data: {
          titleKey: 'superAdminNavAnalytics',
          icon: 'chart-no-axes-column-increasing',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/superadmin-analytics/superadmin-analytics.page').then(
            (m) => m.SuperAdminAnalyticsPage,
          ),
      },
      {
        path: 'crm-comercial',
        data: {
          titleKey: 'superAdminNavCommercialCrm',
          icon: 'briefcase-business',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/commercial-crm/commercial-crm.page').then(
            (m) => m.CommercialCrmPage,
          ),
      },
      {
        path: 'plantillas-consentimiento',
        redirectTo: () =>
          `${WebRoutes.SuperAdmin}/configuracion-global?section=template&view=consent`,
      },
      {
        path: 'documentacion',
        data: {
          titleKey: 'superAdminNavDocumentation',
          icon: 'book-open',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/documentation/documentation.page').then(
            (m) => m.DocumentationPage,
          ),
      },
      {
        path: 'auditoria',
        data: {
          titleKey: 'superAdminNavAudit',
          icon: 'shield-check',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/superadmin-audit/superadmin-audit.page').then(
            (m) => m.SuperAdminAuditPage,
          ),
      },
      {
        path: 'configuracion-global',
        data: {
          titleKey: 'superAdminNavGlobalSettings',
          icon: 'settings',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/platform-settings/platform-settings.page').then(
            (m) => m.PlatformSettingsPage,
          ),
      },
      // SuperAdmin and Developer never enter the admin shell, so `/settings` -- where every other
      // role manages its passkeys -- is unreachable for them: this is their only passkey screen.
      {
        path: 'seguridad',
        data: {
          titleKey: 'accountNavSecurity',
          icon: 'fingerprint-pattern',
        },
        loadComponent: () =>
          import('@features/web/superadmin/presentation/pages/security/security.page').then(
            (m) => m.SecurityPage,
          ),
      },
      { path: '**', redirectTo: '' },
    ],
  },
  {
    path: WebRoutes.MobileClient.slice(1),
    // Developer-only preview tool: renders the mobile app's own screens (served by the separate
    // `mobile` app, see environment.mobileAppUrl) inside a device frame. `Client` no longer needs
    // access here now that `mobile` is its own deployed app rather than a web-hosted preview.
    canActivate: [authGuard, roleGuard([UserRole.Developer])],
    data: { titleKey: 'mobileDeviceEmulatorTitle' },
    loadComponent: () =>
      import('@features/web/mobile-device-emulator/presentation/pages/mobile-device-emulator/mobile-device-emulator.page').then(
        (m) => m.MobileDeviceEmulatorPage,
      ),
  },
  {
    path: WebRoutes.OnboardingUiDesign.slice(1),
    canActivate: [authGuard, roleGuard([UserRole.Developer])],
    data: { titleKey: 'developerFabOnboardingUiDesign' },
    loadComponent: () =>
      import('@features/web/onboarding-ui-design/presentation/pages/onboarding-ui-design/onboarding-ui-design.page').then(
        (m) => m.OnboardingUiDesignPage,
      ),
  },
  {
    path: WebRoutes.ProvetExplorer.slice(1),
    canActivate: [authGuard, roleGuard([UserRole.Developer])],
    data: { titleKey: 'developerFabProvetExplorer' },
    loadComponent: () =>
      import('@features/web/provet-explorer/presentation/pages/provet-explorer/provet-explorer.page').then(
        (m) => m.ProvetExplorerPage,
      ),
  },
  {
    path: '',
    canActivate: [
      authGuard,
      roleGuard([UserRole.Admin, UserRole.ClinicOwner, UserRole.Worker]),
      onboardingRequiredGuard,
      clinicSelectionRequiredGuard,
    ],
    loadComponent: () =>
      import('./shells/admin-shell/admin-shell.page').then((m) => m.AdminShellPage),
    children: [
      { path: '', redirectTo: WebRoutes.Dashboard.slice(1), pathMatch: 'full' },
      {
        path: WebRoutes.Dashboard.slice(1),
        data: { titleKey: 'dashboard' },
        loadComponent: () =>
          import('@features/web/dashboard/presentation/pages/dashboard/dashboard.page').then(
            (m) => m.DashboardPage,
          ),
      },
      {
        path: WebRoutes.Clients.slice(1),
        canActivate: [hasModuleAccessGuard('clients', 'read')],
        data: { titleKey: 'clients' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@features/web/clients/presentation/pages/clients/clients.page').then(
                (m) => m.ClientsPage,
              ),
          },
          {
            path: ':id',
            data: { titleKey: 'clients' },
            loadComponent: () =>
              import('@features/web/clients/presentation/pages/client-detail/client-detail.page').then(
                (m) => m.ClientDetailPage,
              ),
          },
        ],
      },
      {
        path: WebRoutes.Patients.slice(1),
        canActivate: [hasModuleAccessGuard('patients', 'read')],
        data: { titleKey: 'patients' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@features/web/patients/presentation/pages/patients/patients.page').then(
                (m) => m.PatientsPage,
              ),
          },
          {
            path: ':id',
            data: { titleKey: 'patients' },
            loadComponent: () =>
              import('@features/web/patients/presentation/pages/patient-detail/patient-detail.page').then(
                (m) => m.PatientDetailPage,
              ),
          },
          {
            // One route for both entry points: reached from a client record it carries
            // `?from=client:{clientId}`, which only changes the breadcrumb trail and which gated
            // endpoints the sections are fetched through -- see `ConsultationDetailPage`.
            path: ':id/consultations/:consultationId',
            data: { titleKey: 'patients' },
            loadComponent: () =>
              import('@features/web/patients/presentation/pages/consultation-detail/consultation-detail.page').then(
                (m) => m.ConsultationDetailPage,
              ),
          },
        ],
      },
      {
        path: WebRoutes.Appointments.slice(1),
        canActivate: [hasModuleAccessGuard('appointments', 'read')],
        data: { titleKey: 'appointments' },
        loadComponent: () =>
          import('@features/web/appointments/presentation/pages/appointments/appointments.page').then(
            (m) => m.AppointmentsPage,
          ),
      },
      {
        path: WebRoutes.Communication.slice(1),
        canActivate: [hasModuleAccessGuard('communication', 'read')],
        data: { titleKey: 'communication' },
        loadComponent: () =>
          import('@features/web/communication/presentation/pages/communication/communication.page').then(
            (m) => m.CommunicationPage,
          ),
        // A CHILD route, not a sibling `communication/:id`: only a child leaves the parent's route
        // config unchanged when the id appears, so `CommunicationPage` (and with it the inbox list,
        // its poll and the scroll position) is never torn down and rebuilt just to open a thread.
        children: [
          {
            path: ':conversationId',
            data: { titleKey: 'communication' },
            loadComponent: () =>
              import('@features/web/communication/presentation/pages/conversation-thread/conversation-thread.page').then(
                (m) => m.ConversationThreadPage,
              ),
          },
        ],
      },
      {
        path: WebRoutes.Billing.slice(1),
        canActivate: [hasModuleAccessGuard('billing', 'read')],
        data: { titleKey: 'billing' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@features/web/billing/presentation/pages/billing/billing.page').then(
                (m) => m.BillingPage,
              ),
          },
          {
            path: 'estimates/:id',
            data: { titleKey: 'billing' },
            loadComponent: () =>
              import('@features/web/billing/presentation/pages/estimate-detail/estimate-detail.page').then(
                (m) => m.EstimateDetailPage,
              ),
          },
          {
            path: 'invoices/:id',
            data: { titleKey: 'billing' },
            loadComponent: () =>
              import('@features/web/billing/presentation/pages/invoice-detail/invoice-detail.page').then(
                (m) => m.InvoiceDetailPage,
              ),
          },
        ],
      },
      {
        path: WebRoutes.Analytics.slice(1),
        canActivate: [hasModuleAccessGuard('analytics', 'read')],
        data: { titleKey: 'analytics' },
        loadComponent: () =>
          import('@features/web/analytics/presentation/pages/analytics/analytics.page').then(
            (m) => m.AnalyticsPage,
          ),
      },
      {
        path: WebRoutes.Marketing.slice(1),
        canActivate: [hasModuleAccessGuard('marketing', 'read')],
        data: { titleKey: 'marketing' },
        loadComponent: () =>
          import('@features/web/marketing/presentation/pages/marketing/marketing.page').then(
            (m) => m.MarketingPage,
          ),
      },
      // Sin guard de modulo, como Configuracion: el manual no es uno de los modulos que se venden y
      // todo el personal tiene que poder leerlo, sea cual sea el plan de su clinica.
      {
        path: WebRoutes.Documentation.slice(1),
        data: { titleKey: 'documentation' },
        loadComponent: () =>
          import('@features/web/documentation/presentation/pages/documentation/documentation.page').then(
            (m) => m.DocumentationPage,
          ),
      },
      // Bookmarks of the retired clinic `/consent-templates` page land on Settings.
      {
        path: WebRoutes.ConsentTemplates.slice(1),
        pathMatch: 'full',
        redirectTo: WebRoutes.Settings,
      },
      // Deliberately unguarded beyond the admin shell's own role check: every staff role -- Worker
      // included -- needs this page to manage their own passkeys. The clinic-scoped sections are
      // gated inside `SettingsPage` instead (see its `canManageClinic`).
      {
        path: WebRoutes.Settings.slice(1),
        data: { titleKey: 'settings' },
        children: [
          {
            path: '',
            canDeactivate: [settingsUnsavedChangesGuard],
            loadComponent: () =>
              import('@features/web/settings/presentation/pages/settings/settings.page').then(
                (m) => m.SettingsPage,
              ),
          },
          {
            path: 'departamentos/:id',
            canActivate: [hasModuleAccessGuard('settings', 'read')],
            data: { titleKey: 'settings' },
            loadComponent: () =>
              import('@features/web/settings/presentation/pages/department-detail/department-detail.page').then(
                (m) => m.DepartmentDetailPage,
              ),
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
